import { useCallback, useEffect, useMemo } from 'react';
import {
  format,
  fromUnixTime,
  parse,
  addDays,
  min,
} from 'date-fns';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import {
  selectMeteorologyAgroWBSequentialMapsPeriodStart,
  selectMeteorologyAgroWBSequentialMapsPeriodEnd,
  setPeriodStart,
  setPeriodEnd,
  selectMeteorologyAgroWBSequentialMapsVariable,
} from 'redux/reducers/meteorologyAgroWBSequentialMapsReducer';
import useOptions from './useOptions';

export default function usePeriod() {
  const { options } = useOptions();
  const variable = useSelector(selectMeteorologyAgroWBSequentialMapsVariable);
  const disabled = !options || !variable;
  const selectsRange = useMemo(() => {
    if (!options || !variable) return false;
    const variableOptions = options.variables.find((v) => v.value === variable);
    return variableOptions ? variableOptions.period : false;
  }, [options, variable]);
  const periodStart = useSelector(selectMeteorologyAgroWBSequentialMapsPeriodStart);
  const periodEnd = useSelector(selectMeteorologyAgroWBSequentialMapsPeriodEnd);
  const startDate = periodStart ? parse(periodStart, 'yyyy-MM-dd', new Date()) : undefined;
  const endDate = periodEnd ? parse(periodEnd, 'yyyy-MM-dd', new Date()) : undefined;
  const dateIntervals = useMemo(() => {
    if (!options) return [];

    return [{
      start: fromUnixTime(options.startDate),
      end: (startDate && !endDate)
        ? min([fromUnixTime(options.endDate), addDays(startDate, options.maxInterval - 1)])
        : fromUnixTime(options.endDate),
    }];
  }, [options, startDate, endDate]);

  const changePeriodRange = useCallback((newDates: (Date | null)[]) => {
    const [newStartDate, newEndDate] = newDates;
    const newPeriodStart = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : undefined;
    const newPeriodEnd = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStart(newPeriodStart));
    store.dispatch(setPeriodEnd(newPeriodEnd));
  }, []);

  const changePeriod = useCallback((newDate: Date | null) => {
    const newPeriodStart = newDate ? format(newDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStart(newPeriodStart));
    store.dispatch(setPeriodEnd(newPeriodStart));
  }, []);

  useEffect(() => {
    if (options) {
      const defaultStartDate = format(new Date(), 'yyyy-MM-dd');
      const defaultEndDate = format(
        min([fromUnixTime(options.endDate), addDays(new Date(), options.maxInterval - 1)]),
        'yyyy-MM-dd',
      );
      store.dispatch(setPeriodStart(defaultStartDate));
      store.dispatch(setPeriodEnd(defaultEndDate));
    } else {
      store.dispatch(setPeriodStart(format(new Date(), 'yyyy-MM-dd')));
    }
  }, [options]);

  useEffect(() => {
    if (!selectsRange && startDate && startDate !== endDate) {
      changePeriod(startDate);
    }
  }, [selectsRange, startDate, endDate, changePeriod]);

  return {
    startDate,
    endDate,
    dateIntervals,
    changePeriodRange,
    changePeriod,
    disabled,
    selectsRange,
  };
}
